import { useState, useCallback, useEffect, type FC } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import { Spin, message } from 'antd';
import { BookOpen, Database, Check, X, BarChart3 } from 'lucide-react';
import { GuidedTour } from '@/components/onboarding/GuidedTour';
import { usePageTour } from '@/hooks/usePageTour';
import { knowledgeExplorerTourSteps } from '@/lib/tour-data';
import StatCard from '@/components/common/StatCard';
import KnowledgeBrickFilters, { type BrickFilters } from './components/KnowledgeBrickFilters';
import KnowledgeBrickTable, { type KnowledgeBrick } from './components/KnowledgeBrickTable';
import KnowledgeBrickDetailModal from './components/KnowledgeBrickDetailModal';
import KnowledgeBrickEditModal from './components/KnowledgeBrickEditModal';

const EMPTY_FILTERS: BrickFilters = {};

const TOUR_ID = 'admin-knowledge-explorer';

const KnowledgeExplorerPage: FC = () => {
  const tour = usePageTour(TOUR_ID);
  useEffect(() => {
    const handler = () => tour.startTour();
    window.addEventListener('restart-page-tour', handler);
    return () => window.removeEventListener('restart-page-tour', handler);
  }, [tour]);

  const [filters, setFilters] = useState<BrickFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [viewBrick, setViewBrick] = useState<KnowledgeBrick | null>(null);
  const [editBrick, setEditBrick] = useState<KnowledgeBrick | null>(null);

  const query: Record<string, string | number> = { page, limit: pageSize };
  if (filters.category) query.category = filters.category;
  if (filters.sourceType) query.sourceType = filters.sourceType;
  if (filters.persona) query.persona = filters.persona;
  if (filters.isVerified) query.isVerified = filters.isVerified;

  const { data, isLoading, refetch } = useCustom<{
    data: { items: KnowledgeBrick[]; total: number; page: number; limit: number };
  }>({
    url: '/api/v1/admin/growth/knowledge-bricks',
    method: 'get',
    config: { query },
  });

  const { mutate: patchBrick, isLoading: isSaving } = useCustomMutation();
  const { mutate: deleteBrick } = useCustomMutation();

  const items = data?.data?.data?.items ?? [];
  const total = data?.data?.data?.total ?? 0;

  const verified = items.filter((b) => b.isVerified).length;
  const totalUsages = items.reduce((sum, b) => sum + (b.usageCount ?? 0), 0);

  const handleToggleVerified = useCallback(
    (id: string, checked: boolean) => {
      patchBrick(
        { url: `/api/v1/admin/growth/knowledge-bricks/${id}`, method: 'patch', values: { isVerified: checked } },
        { onSuccess: () => { message.success(checked ? 'Brique vérifiée' : 'Vérification retirée'); refetch(); } },
      );
    },
    [patchBrick, refetch],
  );

  const handleSave = useCallback(
    (id: string, values: Partial<KnowledgeBrick>) => {
      patchBrick(
        { url: `/api/v1/admin/growth/knowledge-bricks/${id}`, method: 'patch', values },
        { onSuccess: () => { message.success('Brique mise à jour'); setEditBrick(null); refetch(); } },
      );
    },
    [patchBrick, refetch],
  );

  const handleDelete = useCallback(
    (id: string) => {
      deleteBrick(
        { url: `/api/v1/admin/growth/knowledge-bricks/${id}`, method: 'delete', values: {} },
        { onSuccess: () => { message.success('Brique supprimée'); refetch(); } },
      );
    },
    [deleteBrick, refetch],
  );

  const handlePageChange = (p: number, ps: number) => { setPage(p); setPageSize(ps); };
  const handleFilterChange = (f: BrickFilters) => { setFilters(f); setPage(1); };
  const handleFilterReset = () => { setFilters(EMPTY_FILTERS); setPage(1); };

  if (isLoading && !items.length) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="fc-gradient-header rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3" data-tour="knowledge-explorer-header">
          <BookOpen className="w-6 h-6" />
          <div>
            <h1 className="text-xl font-bold">Knowledge Explorer</h1>
            <p className="mt-0.5 text-xs italic text-blue-200">Explorateur de connaissances</p>
            <p className="mt-0.5 text-sm text-blue-100">Explorez et validez la base de connaissance du Growth Engine</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Total briques" value={total} icon={Database} iconColor="text-blue-600 dark:text-blue-400" iconBg="bg-blue-50 dark:bg-blue-900/30" />
        <StatCard title="Vérifiées" value={verified} icon={Check} iconColor="text-emerald-600 dark:text-emerald-400" iconBg="bg-emerald-50 dark:bg-emerald-900/30" />
        <StatCard title="Non vérifiées" value={total - verified} icon={X} iconColor="text-amber-600 dark:text-amber-400" iconBg="bg-amber-50 dark:bg-amber-900/30" />
        <StatCard title="Usages totaux" value={totalUsages} icon={BarChart3} iconColor="text-purple-600 dark:text-purple-400" iconBg="bg-purple-50 dark:bg-purple-900/30" />
      </div>

      <KnowledgeBrickFilters filters={filters} onChange={handleFilterChange} onReset={handleFilterReset} />

      <KnowledgeBrickTable
        data={items}
        total={total}
        page={page}
        pageSize={pageSize}
        loading={isLoading}
        onPageChange={handlePageChange}
        onView={setViewBrick}
        onEdit={setEditBrick}
        onDelete={handleDelete}
        onToggleVerified={handleToggleVerified}
      />

      <KnowledgeBrickDetailModal brick={viewBrick} open={!!viewBrick} onClose={() => setViewBrick(null)} />
      <KnowledgeBrickEditModal brick={editBrick} open={!!editBrick} saving={isSaving} onClose={() => setEditBrick(null)} onSave={handleSave} />
      <GuidedTour tourId={TOUR_ID} steps={knowledgeExplorerTourSteps} isActive={tour.isActive} onComplete={tour.completeTour} onSkip={tour.skipTour} />
    </div>
  );
};

export default KnowledgeExplorerPage;
